import { useState, useMemo } from "react";
import { trpc } from "../lib/trpc.js";
import {
  Loader2, AlertTriangle, Plus, MessageSquare, Clock,
  CheckCircle2, XCircle, ArrowRight, ChevronDown, ChevronUp,
  Send, X, Flag, User, Tag, FileText, Filter, Search,
  Inbox, CircleDot, Eye, LayoutGrid, Zap, TrendingUp,
  Calendar, Hash,
} from "lucide-react";

// ─── Types ──────────────────────────────────────────────────

interface BacklogNote {
  agent: string;
  text: string;
  timestamp: string;
}

interface BacklogItem {
  id: string;
  created_by: string;
  assigned_to: string | null;
  title: string;
  description: string | null;
  priority: string;
  category: string;
  status: string;
  acceptance_criteria: string | null;
  notes: BacklogNote[];
  created_at: number;
  updated_at: number | null;
  completed_at: number | null;
  due_date: number | null;
}

// ─── Config ─────────────────────────────────────────────────

const priorityConfig: Record<string, { label: string; color: string; bg: string; border: string; icon: typeof Flag }> = {
  P0: { label: "Critico",  color: "text-red-400",    bg: "bg-red-500/15",    border: "border-red-500/30",   icon: Zap },
  P1: { label: "Urgente",  color: "text-amber-400",  bg: "bg-amber-500/15",  border: "border-amber-500/30", icon: TrendingUp },
  P2: { label: "Normal",   color: "text-blue-400",   bg: "bg-blue-500/15",   border: "border-blue-500/30",  icon: CircleDot },
  P3: { label: "Baixa",    color: "text-slate-400",  bg: "bg-slate-500/15",  border: "border-slate-500/30", icon: Clock },
};

const categoryConfig: Record<string, { label: string; color: string; bg: string }> = {
  bug:            { label: "Bug",             color: "text-red-400",     bg: "bg-red-500/10" },
  improvement:    { label: "Melhoria",        color: "text-blue-400",    bg: "bg-blue-500/10" },
  feature:        { label: "Feature",         color: "text-emerald-400", bg: "bg-emerald-500/10" },
  debt:           { label: "Divida Tecnica",  color: "text-amber-400",   bg: "bg-amber-500/10" },
  question:       { label: "Pergunta",        color: "text-purple-400",  bg: "bg-purple-500/10" },
  investigation:  { label: "Investigacao",    color: "text-cyan-400",    bg: "bg-cyan-500/10" },
  campaign_ops:   { label: "Campaign Ops",    color: "text-orange-400",  bg: "bg-orange-500/10" },
  delegated:      { label: "Delegada",        color: "text-indigo-400",  bg: "bg-indigo-500/10" },
};

const statusColumns = [
  { key: "backlog",     label: "Backlog",       color: "#64748B", icon: Inbox,        gradient: "from-slate-500/8 to-transparent" },
  { key: "in_progress", label: "Em Progresso",  color: "#3B82F6", icon: CircleDot,    gradient: "from-blue-500/8 to-transparent" },
  { key: "review",      label: "Revisao",       color: "#F59E0B", icon: Eye,          gradient: "from-amber-500/8 to-transparent" },
  { key: "done",        label: "Concluido",     color: "#22C55E", icon: CheckCircle2, gradient: "from-emerald-500/8 to-transparent" },
];

const agentLabels: Record<string, string> = {
  product_owner: "P.O. 360",
  megan_developer: "Megan Dev",
  developer: "Developer",
  user: "Voce",
  system: "Sistema",
  ytrader: "YTrader",
  campaign_operations: "Campaign Ops",
  auditor: "Auditor",
  strategist: "Strategist",
  creatives: "Creatives",
  publish_manager: "Publisher",
};

const agentColors: Record<string, string> = {
  product_owner: "#F59E0B",
  megan_developer: "#6366F1",
  developer: "#818CF8",
  user: "#22C55E",
  system: "#64748B",
  ytrader: "#F97316",
  campaign_operations: "#FB923C",
  auditor: "#A78BFA",
  strategist: "#34D399",
  creatives: "#EC4899",
  publish_manager: "#06B6D4",
};

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "agora";
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d`;
  return `${Math.floor(days / 30)}mo`;
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString("pt-BR", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

// ─── Create Item Modal ──────────────────────────────────────

interface CreateModalProps {
  onClose: () => void;
  onCreated: () => void;
}

function CreateItemModal({ onClose, onCreated }: CreateModalProps) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("P2");
  const [category, setCategory] = useState("improvement");
  const [assignedTo, setAssignedTo] = useState("");
  const [acceptanceCriteria, setAcceptanceCriteria] = useState("");

  const createMutation = trpc.brain.createBacklogItem.useMutation({
    onSuccess: () => {
      onCreated();
      onClose();
    },
  });

  const handleSubmit = () => {
    if (!title.trim()) return;
    createMutation.mutate({
      created_by: "user",
      title: title.trim(),
      description: description.trim() || undefined,
      priority: priority as "P0" | "P1" | "P2" | "P3",
      category: category as "bug" | "improvement" | "feature" | "debt" | "question" | "investigation",
      assigned_to: assignedTo || undefined,
      acceptance_criteria: acceptanceCriteria.trim() || undefined,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200" onClick={onClose}>
      <div
        className="bg-card border border-border rounded-2xl w-full max-w-lg mx-4 shadow-2xl animate-in slide-in-from-bottom-4 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center">
              <Plus className="w-4 h-4 text-primary" />
            </div>
            <h3 className="text-lg font-bold text-foreground">Novo Item</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Titulo</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Descreva o item brevemente..."
              className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all"
              autoFocus
            />
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Descricao</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Detalhes, contexto, links..."
              rows={3}
              className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 resize-none transition-all"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Prioridade</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
              >
                {Object.entries(priorityConfig).map(([key, cfg]) => (
                  <option key={key} value={key}>{key} — {cfg.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Categoria</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
              >
                {Object.entries(categoryConfig).map(([key, cfg]) => (
                  <option key={key} value={key}>{cfg.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Responsavel</label>
              <select
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
              >
                <option value="">Nao atribuido</option>
                {Object.entries(agentLabels).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Criterios de Aceite (opcional)</label>
            <textarea
              value={acceptanceCriteria}
              onChange={(e) => setAcceptanceCriteria(e.target.value)}
              placeholder="- [ ] Criterio 1&#10;- [ ] Criterio 2"
              rows={2}
              className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 resize-none font-mono transition-all"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-5 border-t border-border">
          <button onClick={onClose} className="px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted">
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={!title.trim() || createMutation.isPending}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-primary/25"
          >
            {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Criar Item
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Note Input ─────────────────────────────────────────────

function NoteInput({ itemId, onAdded }: { itemId: string; onAdded: () => void }) {
  const [text, setText] = useState("");
  const addNote = trpc.brain.addBacklogNote.useMutation({
    onSuccess: () => {
      setText("");
      onAdded();
    },
  });

  return (
    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border/20">
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && text.trim()) {
            addNote.mutate({ id: itemId, agent: "user", text: text.trim() });
          }
        }}
        placeholder="Adicionar nota..."
        className="flex-1 bg-background/50 border border-border/50 rounded-lg px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/30 transition-all"
      />
      <button
        onClick={() => {
          if (text.trim()) addNote.mutate({ id: itemId, agent: "user", text: text.trim() });
        }}
        disabled={!text.trim() || addNote.isPending}
        className="p-2 rounded-lg hover:bg-primary/10 text-primary disabled:opacity-30 transition-all"
      >
        {addNote.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

// ─── Agent Badge ────────────────────────────────────────────

function AgentBadge({ agent, size = "sm" }: { agent: string; size?: "sm" | "md" }) {
  const color = agentColors[agent] || "#64748B";
  const label = agentLabels[agent] || agent;
  const isSmall = size === "sm";

  return (
    <div className="flex items-center gap-1.5">
      <div
        className={`rounded-full flex items-center justify-center font-bold text-white ${isSmall ? "w-5 h-5 text-[8px]" : "w-6 h-6 text-[9px]"}`}
        style={{ backgroundColor: color }}
        title={label}
      >
        {label.charAt(0).toUpperCase()}
      </div>
      <span className={`font-medium truncate ${isSmall ? "text-[10px]" : "text-xs"}`} style={{ color }}>
        {label}
      </span>
    </div>
  );
}

// ─── Backlog Card ───────────────────────────────────────────

function BacklogCard({ item, columnColor, onRefresh }: { item: BacklogItem; columnColor: string; onRefresh: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const pConfig = priorityConfig[item.priority] || priorityConfig.P2;
  const cConfig = categoryConfig[item.category] || categoryConfig.improvement;
  const updateMutation = trpc.brain.updateBacklogItem.useMutation({ onSuccess: onRefresh });
  const PriorityIcon = pConfig.icon;

  const nextStatus: Record<string, string> = {
    backlog: "in_progress",
    in_progress: "review",
    review: "done",
  };

  const nextStatusLabel: Record<string, string> = {
    backlog: "Iniciar",
    in_progress: "Revisar",
    review: "Aprovar",
  };

  const nextStatusIcon: Record<string, typeof ArrowRight> = {
    backlog: CircleDot,
    in_progress: Eye,
    review: CheckCircle2,
  };

  return (
    <div
      className="bg-card/90 backdrop-blur-sm border rounded-xl overflow-hidden transition-all duration-200 hover:shadow-lg hover:shadow-black/10 group"
      style={{ borderColor: `${columnColor}20` }}
    >
      {/* Priority stripe */}
      <div className="h-0.5 w-full" style={{ backgroundColor: pConfig.color.includes("red") ? "#EF4444" : pConfig.color.includes("amber") ? "#F59E0B" : pConfig.color.includes("blue") ? "#3B82F6" : "#64748B" }} />

      <div className="p-3.5">
        {/* Header row: priority + title */}
        <div className="flex items-start gap-2.5 mb-2">
          <div
            className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 mt-0.5 ${pConfig.bg}`}
            title={`${item.priority} — ${pConfig.label}`}
          >
            <PriorityIcon className={`w-3 h-3 ${pConfig.color}`} />
          </div>
          <div className="flex-1 min-w-0">
            <span
              className="text-[13px] font-semibold text-foreground leading-snug cursor-pointer hover:text-primary transition-colors line-clamp-2"
              onClick={() => setExpanded(!expanded)}
            >
              {item.title}
            </span>
          </div>
          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${pConfig.bg} ${pConfig.color} ${pConfig.border} border`}>
            {item.priority}
          </span>
        </div>

        {/* Description preview */}
        {item.description && !expanded && (
          <p className="text-[11px] text-muted-foreground leading-relaxed line-clamp-2 mb-2.5 ml-8">
            {item.description}
          </p>
        )}

        {/* Meta row */}
        <div className="flex items-center justify-between ml-8">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${cConfig.bg} ${cConfig.color}`}>
              {cConfig.label}
            </span>
            {item.assigned_to && (
              <AgentBadge agent={item.assigned_to} size="sm" />
            )}
          </div>
          <div className="flex items-center gap-2">
            {item.notes && item.notes.length > 0 && (
              <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                <MessageSquare className="w-3 h-3" />
                {item.notes.length}
              </span>
            )}
            <span className="text-[10px] text-muted-foreground font-mono" title={formatDate(item.created_at)}>
              {relativeTime(item.created_at)}
            </span>
          </div>
        </div>

        {/* Action buttons */}
        {item.status !== "done" && item.status !== "rejected" && (
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border/20 ml-8">
            {nextStatus[item.status] && (
              <button
                onClick={() => updateMutation.mutate({ id: item.id, status: nextStatus[item.status], agent: "user" })}
                disabled={updateMutation.isPending}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-primary bg-primary/10 rounded-lg hover:bg-primary/20 transition-all disabled:opacity-50"
              >
                {updateMutation.isPending ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  (() => {
                    const NextIcon = nextStatusIcon[item.status] || ArrowRight;
                    return <NextIcon className="w-3 h-3" />;
                  })()
                )}
                {nextStatusLabel[item.status]}
              </button>
            )}
            {item.status === "review" && (
              <button
                onClick={() => updateMutation.mutate({ id: item.id, status: "rejected", agent: "user" })}
                disabled={updateMutation.isPending}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-red-400 bg-red-500/10 rounded-lg hover:bg-red-500/20 transition-all disabled:opacity-50"
              >
                <XCircle className="w-3 h-3" />
                Rejeitar
              </button>
            )}
            <button
              onClick={() => setExpanded(!expanded)}
              className="ml-auto p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all"
            >
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          </div>
        )}

        {/* Done/Rejected state expand */}
        {(item.status === "done" || item.status === "rejected") && (
          <div className="flex justify-end mt-2 ml-8">
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all"
            >
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          </div>
        )}

        {/* Expanded details */}
        {expanded && (
          <div className="mt-3 pt-3 border-t border-border/20 space-y-3 ml-8 animate-in fade-in slide-in-from-top-2 duration-200">
            {/* Full description */}
            {item.description && (
              <div className="rounded-lg bg-background/50 p-3">
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider block mb-1.5">Descricao</span>
                <p className="text-[12px] text-foreground/80 whitespace-pre-wrap leading-relaxed">{item.description}</p>
              </div>
            )}

            {/* Acceptance criteria */}
            {item.acceptance_criteria && (
              <div className="rounded-lg bg-background/50 p-3">
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1 mb-1.5">
                  <FileText className="w-3 h-3" /> Criterios de Aceite
                </span>
                <pre className="text-[11px] text-foreground/80 whitespace-pre-wrap font-mono leading-relaxed">
                  {item.acceptance_criteria}
                </pre>
              </div>
            )}

            {/* Created by + dates */}
            <div className="flex items-center gap-4 text-[10px] text-muted-foreground">
              <span className="flex items-center gap-1">
                <User className="w-3 h-3" />
                Criado por <span className="font-medium" style={{ color: agentColors[item.created_by] || "#64748B" }}>
                  {agentLabels[item.created_by] || item.created_by}
                </span>
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatDate(item.created_at)}
              </span>
              {item.completed_at && (
                <span className="flex items-center gap-1 text-emerald-400">
                  <CheckCircle2 className="w-3 h-3" />
                  {formatDate(item.completed_at)}
                </span>
              )}
            </div>

            {/* Notes (debate) */}
            {item.notes && item.notes.length > 0 && (
              <div>
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1 mb-2">
                  <MessageSquare className="w-3 h-3" /> Notas ({item.notes.length})
                </span>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {item.notes.map((note, i) => (
                    <div key={i} className="flex gap-2.5 text-[11px] p-2 rounded-lg bg-background/30">
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold text-white shrink-0 mt-0.5"
                        style={{ backgroundColor: agentColors[note.agent] || "#64748B" }}
                      >
                        {(agentLabels[note.agent] || note.agent).charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="font-medium block" style={{ color: agentColors[note.agent] || "#64748B" }}>
                          {agentLabels[note.agent] || note.agent}
                        </span>
                        <span className="text-foreground/80 block mt-0.5">{note.text}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add note */}
            <NoteInput itemId={item.id} onAdded={onRefresh} />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Backlog Column ─────────────────────────────────────────

function BacklogColumn({ status, label, color, gradient, icon: ColumnIcon, items, onRefresh }: {
  status: string;
  label: string;
  color: string;
  gradient: string;
  icon: typeof Inbox;
  items: BacklogItem[];
  onRefresh: () => void;
}) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div
      className={`min-w-[300px] max-w-[340px] flex-1 bg-gradient-to-b ${gradient} border rounded-xl overflow-hidden flex flex-col transition-all duration-300`}
      style={{ borderColor: `${color}20` }}
    >
      {/* Column Header */}
      <div
        className="px-4 py-3 border-b flex items-center justify-between cursor-pointer hover:bg-white/[0.02] transition-colors"
        style={{ borderColor: `${color}15` }}
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${color}15` }}
          >
            <ColumnIcon className="w-4 h-4" style={{ color }} />
          </div>
          <div>
            <span className="text-sm font-bold text-foreground block">{label}</span>
            <span className="text-[10px] text-muted-foreground">
              {items.length} {items.length === 1 ? "item" : "itens"}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="text-xs font-bold px-2.5 py-1 rounded-full"
            style={{ color, backgroundColor: `${color}15` }}
          >
            {items.length}
          </span>
          <ChevronDown
            className={`w-3.5 h-3.5 text-muted-foreground transition-transform duration-200 ${collapsed ? "-rotate-90" : ""}`}
          />
        </div>
      </div>

      {/* Cards */}
      {!collapsed && (
        <div className="p-3 space-y-2.5 overflow-y-auto max-h-[600px] flex-1">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground/40">
              <div className="w-12 h-12 rounded-full bg-muted/20 flex items-center justify-center mb-3">
                <ColumnIcon className="w-5 h-5" />
              </div>
              <span className="text-xs font-medium">Nenhum item</span>
            </div>
          ) : (
            items.map((item, i) => (
              <div
                key={item.id}
                className="animate-in fade-in slide-in-from-bottom-1"
                style={{ animationDelay: `${i * 50}ms` }}
              >
                <BacklogCard item={item} columnColor={color} onRefresh={onRefresh} />
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ─── Summary Bar ────────────────────────────────────────────

function SummaryBar({ summary, activeFilter, onFilterChange }: {
  summary: { total_open: number; by_priority: Record<string, number>; by_agent: Record<string, number>; created_last_7d: number; completed_last_7d: number } | undefined;
  activeFilter: string | null;
  onFilterChange: (filter: string | null) => void;
}) {
  if (!summary) return null;

  return (
    <div className="flex items-center gap-3 flex-wrap">
      {/* Total open */}
      <button
        onClick={() => onFilterChange(activeFilter ? null : "all")}
        className={`flex items-center gap-2 px-3.5 py-2 rounded-xl border transition-all ${
          !activeFilter
            ? "bg-primary/10 border-primary/30 text-primary"
            : "bg-card border-border text-foreground hover:border-primary/20"
        }`}
      >
        <LayoutGrid className="w-3.5 h-3.5" />
        <span className="text-xs font-bold">{summary.total_open}</span>
        <span className="text-xs font-medium">abertos</span>
      </button>

      {/* Priority filters */}
      {Object.entries(summary.by_priority || {}).map(([p, count]) => {
        const pc = priorityConfig[p];
        if (!pc || count === 0) return null;
        const isActive = activeFilter === p;
        return (
          <button
            key={p}
            onClick={() => onFilterChange(isActive ? null : p)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border transition-all ${
              isActive
                ? `${pc.bg} ${pc.border} ring-1 ring-offset-1 ring-offset-background`
                : `${pc.bg} ${pc.border} opacity-70 hover:opacity-100`
            }`}
            style={isActive ? { ringColor: pc.color } : undefined}
          >
            <span className={`text-[10px] font-bold ${pc.color}`}>{p}</span>
            <span className="text-[10px] font-medium text-foreground">{count}</span>
          </button>
        );
      })}

      {/* 7d stats */}
      <div className="flex items-center gap-3 px-3.5 py-2 bg-card border border-border rounded-xl ml-auto">
        <div className="flex items-center gap-1.5">
          <Hash className="w-3 h-3 text-muted-foreground" />
          <span className="text-[10px] text-muted-foreground font-medium">7 dias:</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-emerald-400 font-bold">+{summary.created_last_7d}</span>
          <span className="text-[10px] text-muted-foreground">criados</span>
        </div>
        <span className="text-[10px] text-border">|</span>
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-blue-400 font-bold">{summary.completed_last_7d}</span>
          <span className="text-[10px] text-muted-foreground">concluidos</span>
        </div>
      </div>
    </div>
  );
}

// ─── Main BacklogView ───────────────────────────────────────

export function BacklogView() {
  const [showCreate, setShowCreate] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [priorityFilter, setPriorityFilter] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = trpc.brain.getBacklogKanban.useQuery(
    undefined,
    { refetchInterval: 30_000, retry: false }
  );

  // Filter items
  const filteredColumns = useMemo(() => {
    const columns = data?.columns || {};
    if (!searchTerm && !priorityFilter) return columns;

    const filtered: Record<string, BacklogItem[]> = {};
    for (const [status, items] of Object.entries(columns)) {
      filtered[status] = (items as BacklogItem[]).filter((item) => {
        const matchesSearch = !searchTerm ||
          item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (item.description || "").toLowerCase().includes(searchTerm.toLowerCase());
        const matchesPriority = !priorityFilter || item.priority === priorityFilter;
        return matchesSearch && matchesPriority;
      });
    }
    return filtered;
  }, [data?.columns, searchTerm, priorityFilter]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex gap-4 overflow-x-auto pb-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="min-w-[300px] flex-1 bg-card border border-border rounded-xl p-4 animate-pulse">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-border rounded-lg" />
                <div className="h-5 w-24 bg-border rounded" />
              </div>
              <div className="space-y-3">
                {[...Array(2)].map((_, j) => (
                  <div key={j} className="h-28 bg-border/50 rounded-xl" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center">
          <AlertTriangle className="w-8 h-8 text-amber-400" />
        </div>
        <p className="text-sm text-muted-foreground font-medium">Erro ao carregar Backlog</p>
        <button
          onClick={() => refetch()}
          className="text-xs text-primary hover:underline font-medium px-4 py-2 bg-primary/10 rounded-lg"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  const summary = data?.summary;

  return (
    <div className="space-y-4">
      {/* Top bar: Summary + Search + Create */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <SummaryBar summary={summary} activeFilter={priorityFilter} onFilterChange={setPriorityFilter} />
      </div>

      {/* Search + Create row */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar no backlog..."
            className="w-full bg-card border border-border rounded-xl pl-9 pr-3 py-2.5 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/30 transition-all"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-muted"
            >
              <X className="w-3 h-3 text-muted-foreground" />
            </button>
          )}
        </div>

        {(searchTerm || priorityFilter) && (
          <button
            onClick={() => { setSearchTerm(""); setPriorityFilter(null); }}
            className="flex items-center gap-1.5 px-3 py-2.5 text-[11px] font-medium text-muted-foreground hover:text-foreground bg-card border border-border rounded-xl transition-all"
          >
            <Filter className="w-3 h-3" />
            Limpar filtros
          </button>
        )}

        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-primary rounded-xl hover:bg-primary/90 transition-all shadow-lg shadow-primary/20 shrink-0 ml-auto"
        >
          <Plus className="w-4 h-4" />
          Novo Item
        </button>
      </div>

      {/* Kanban Columns */}
      <div className="flex gap-4 overflow-x-auto pb-4 -mx-2 px-2">
        {statusColumns.map((col) => (
          <BacklogColumn
            key={col.key}
            status={col.key}
            label={col.label}
            color={col.color}
            gradient={col.gradient}
            icon={col.icon}
            items={(filteredColumns[col.key] || []) as BacklogItem[]}
            onRefresh={refetch}
          />
        ))}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <CreateItemModal onClose={() => setShowCreate(false)} onCreated={refetch} />
      )}
    </div>
  );
}
